// Polls tool (Meeting Tools). Host creates/launches/ends polls and sees the
// live tally; attendees vote and see results when the host shares them. All
// state + the host-authoritative tally live in pollStore (outside React); this
// component only renders it, so nothing here touches the video path. Handlers
// are stable (useCallback) and identify their target via data-* attributes, per
// the render discipline (no inline function props).

import { ChangeEvent, memo, MouseEvent, useCallback, useState } from 'react';

import { useCallStore } from '../../store/callStore';
import {
  addOption,
  createDraft,
  endPoll,
  exportPollCsv,
  launchPoll,
  type PollView,
  removeOption,
  removePoll,
  setOptionLabel,
  setShowResults,
  updateDraft,
  usePollStore,
  vote,
} from '../../store/pollStore';
import { IconClose, IconDownload, IconPlus, IconTrash } from '../../ui/icons';

export const PollsTool = memo(function PollsTool() {
  const isHost = useCallStore((s) => s.isHost);
  const polls = usePollStore((s) => s.polls);
  const [editId, setEditId] = useState<string | null>(null);

  const create = useCallback(() => setEditId(createDraft()), []);
  const edit = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    setEditId(e.currentTarget.dataset.pollId ?? null);
  }, []);
  const closeEditor = useCallback(() => setEditId(null), []);

  const editing = editId ? polls.find((p) => p.id === editId) : null;
  if (editing) return <PollEditor poll={editing} onDone={closeEditor} />;

  return (
    <div className="bz-polls">
      {polls.length === 0 && (
        <p className="bz-mt-note">
          {isHost
            ? 'No polls yet. Create one to ask the room a question — everyone can vote, and results stay in this meeting.'
            : 'No polls yet. When the host starts one, it will appear here.'}
        </p>
      )}
      {polls.map((p) => (
        <PollCard key={p.id} poll={p} isHost={isHost} onEdit={edit} />
      ))}
      {isHost && (
        <button type="button" className="bz-polls-create" onClick={create}>
          <IconPlus size={17} />
          Create a poll
        </button>
      )}
    </div>
  );
});

// ── Editor (host, draft) ─────────────────────────────────────────────────────
const PollEditor = memo(function PollEditor({
  poll,
  onDone,
}: {
  poll: PollView;
  onDone: () => void;
}) {
  const id = poll.id;
  const onTitle = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => updateDraft(id, { title: e.target.value }),
    [id],
  );
  const onOption = useCallback(
    (e: ChangeEvent<HTMLInputElement>) =>
      setOptionLabel(id, e.currentTarget.dataset.optId!, e.target.value),
    [id],
  );
  const onRemoveOpt = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => removeOption(id, e.currentTarget.dataset.optId!),
    [id],
  );
  const onAdd = useCallback(() => addOption(id), [id]);
  const onAnon = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => updateDraft(id, { anonymous: e.target.checked }),
    [id],
  );
  const onLaunch = useCallback(() => {
    launchPoll(id);
    if (poll.title.trim() && poll.options.filter((o) => o.label.trim()).length >= 2) onDone();
  }, [id, poll, onDone]);
  const onSave = useCallback(() => onDone(), [onDone]);
  const onDelete = useCallback(() => {
    removePoll(id);
    onDone();
  }, [id, onDone]);

  return (
    <div className="bz-poll-editor">
      <label className="bz-poll-field">
        <span className="bz-poll-field-label">Title</span>
        <textarea
          className="bz-poll-title-input"
          placeholder="Ask a question"
          value={poll.title}
          onChange={onTitle}
          maxLength={300}
          rows={2}
        />
      </label>
      <div className="bz-poll-options-edit">
        {poll.options.map((o, i) => (
          <div key={o.id} className="bz-poll-option-edit">
            <span className="bz-poll-option-num">{i + 1}</span>
            <input
              className="bz-poll-option-input"
              placeholder={`Option ${i + 1}`}
              value={o.label}
              data-opt-id={o.id}
              onChange={onOption}
              maxLength={300}
            />
            {poll.options.length > 2 && (
              <button
                type="button"
                className="bz-poll-option-del"
                data-opt-id={o.id}
                onClick={onRemoveOpt}
                aria-label="Remove option"
              >
                <IconClose size={13} />
              </button>
            )}
          </div>
        ))}
      </div>
      {poll.options.length < 10 && (
        <button type="button" className="bz-poll-addopt" onClick={onAdd}>
          <IconPlus size={15} />
          Add an option
        </button>
      )}
      <label className="bz-poll-switch">
        <input type="checkbox" checked={poll.anonymous} onChange={onAnon} />
        <span className="bz-poll-switch-track" aria-hidden="true" />
        <span>Responses appear without names</span>
      </label>
      <p className="bz-poll-hint">
        {poll.anonymous
          ? 'Votes are not attributed to names in the results or to other participants.'
          : 'Names and responses are visible to you, the host.'}
      </p>
      <div className="bz-poll-editor-actions">
        <button type="button" className="bz-poll-icon-btn" onClick={onDelete} aria-label="Delete poll">
          <IconTrash size={17} />
        </button>
        <button type="button" className="bz-poll-textbtn" onClick={onSave}>
          Save
        </button>
        <button type="button" className="bz-poll-launch" onClick={onLaunch}>
          Launch
        </button>
      </div>
    </div>
  );
});

// ── Poll card (list item) ────────────────────────────────────────────────────
const PollCard = memo(function PollCard({
  poll,
  isHost,
  onEdit,
}: {
  poll: PollView;
  isHost: boolean;
  onEdit: (e: MouseEvent<HTMLButtonElement>) => void;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const canSeeCounts = isHost || poll.showResults;
  const live = poll.state === 'live';
  const voted = poll.myVote !== null;

  const onSelect = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    setSelected(e.currentTarget.dataset.optId ?? null);
  }, []);
  const onVote = useCallback(() => {
    if (selected) vote(poll.id, selected);
  }, [selected, poll.id]);
  const onLaunch = useCallback(() => launchPoll(poll.id), [poll.id]);
  const onEnd = useCallback(() => endPoll(poll.id), [poll.id]);
  const onDelete = useCallback(() => removePoll(poll.id), [poll.id]);
  const onExport = useCallback(() => exportPollCsv(poll.id), [poll.id]);
  const onToggleShow = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setShowResults(poll.id, e.target.checked),
    [poll.id],
  );

  return (
    <div className="bz-poll-card">
      <div className="bz-poll-card-head">
        <span className={`bz-poll-badge bz-poll-badge-${poll.state}`}>
          {poll.state === 'draft' ? 'Draft' : poll.state === 'live' ? 'Live' : 'Closed'}
        </span>
        {poll.anonymous && <span className="bz-poll-anon">Anonymous</span>}
      </div>
      <h4 className="bz-poll-q">{poll.title || 'Untitled poll'}</h4>

      {poll.state === 'draft' ? (
        <>
          <ul className="bz-poll-draft-opts">
            {poll.options.map((o) => (
              <li key={o.id}>{o.label || <em>empty</em>}</li>
            ))}
          </ul>
          <div className="bz-poll-actions">
            <button type="button" className="bz-poll-icon-btn" onClick={onDelete} aria-label="Delete">
              <IconTrash size={17} />
            </button>
            <button type="button" className="bz-poll-textbtn" data-poll-id={poll.id} onClick={onEdit}>
              Edit
            </button>
            <button type="button" className="bz-poll-launch" onClick={onLaunch}>
              Launch
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="bz-poll-opts">
            {poll.options.map((o) => {
              const count = poll.counts[o.id] ?? 0;
              const pct = poll.total > 0 ? Math.round((count / poll.total) * 100) : 0;
              const isChoice = live && !voted;
              const marked = voted ? poll.myVote === o.id : selected === o.id;
              return (
                <button
                  key={o.id}
                  type="button"
                  className={`bz-poll-opt${marked ? ' is-marked' : ''}${isChoice ? ' is-choice' : ''}`}
                  data-opt-id={o.id}
                  onClick={isChoice ? onSelect : undefined}
                  disabled={!isChoice}
                >
                  <span className="bz-poll-opt-radio" aria-hidden="true" />
                  <span className="bz-poll-opt-label">{o.label}</span>
                  {canSeeCounts && <span className="bz-poll-opt-count">{count}</span>}
                  {canSeeCounts && (
                    // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop -- leaf DOM node, low-frequency (updates on vote), never a component prop
                    <span className="bz-poll-opt-bar" style={{ width: `${pct}%` }} aria-hidden="true" />
                  )}
                </button>
              );
            })}
          </div>

          {canSeeCounts && (
            <p className="bz-poll-total">
              {poll.total} {poll.total === 1 ? 'vote' : 'votes'}
            </p>
          )}
          {live && !voted && (
            <button type="button" className="bz-poll-launch" onClick={onVote} disabled={!selected}>
              Vote
            </button>
          )}
          {live && voted && !canSeeCounts && (
            <p className="bz-poll-hint">Your response was recorded. Results are hidden by the host.</p>
          )}

          {isHost && (
            <div className="bz-poll-host-controls">
              <label className="bz-poll-switch">
                <input type="checkbox" checked={poll.showResults} onChange={onToggleShow} />
                <span className="bz-poll-switch-track" aria-hidden="true" />
                <span>Show everyone the results</span>
              </label>
              <div className="bz-poll-actions">
                <button type="button" className="bz-poll-icon-btn" onClick={onDelete} aria-label="Delete">
                  <IconTrash size={17} />
                </button>
                <button
                  type="button"
                  className="bz-poll-icon-btn"
                  onClick={onExport}
                  aria-label="Export results (CSV)"
                >
                  <IconDownload size={16} />
                </button>
                {live && (
                  <button type="button" className="bz-poll-textbtn" onClick={onEnd}>
                    End the poll
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
});
